<template>
    <div>
        <hts-data-table
            ref="dataTable"
            :headers="headers"
            :server="server"
            use-selection
            :query-extend="queryExtend"
            :use-archive="usePaymentNoteArchive"
            store-name="tripDataTable"
            store-key="PaymentNote" />
        <hts-modal
            title="Chọn loại Hóa Đơn đồng bộ qua Freightek"
            :show="freightekDialog.visible">
            <hts-form-radio
                v-model="freightekDialog.type"
                value-key="value"
                :items="freightekOptions" />
            <div class="mt-2">
                <hts-form-button
                    color="primary"
                    :loading="freightekDialog.loading"
                    @click="createInvoiceFreightek">
                    {{ $t('labels.confirm') }}
                </hts-form-button>
                <hts-form-button @click="freightekDialog.visible = false">
                    {{ $t('labels.close') }}
                </hts-form-button>
            </div>
        </hts-modal>
    </div>
</template>
<script setup lang="ts">
import layoutPermissions from '@/data/permissions/layout-permissions'
import ActionList from '~/components/data-table/-components/action-list.vue'
import InvoiceStatus from './-components/InvoiceStatus.vue'

const { $formatters } = useNuxtApp()
const props = defineProps({
  trip: Object,
  type: {
    type: String,
    required: true
  }
})
const emits = defineEmits(['view', 'edit'])

const { trip, type } = toRefs(props)

const dataTable = ref()
const deletePaymentNote = ref()
const queryExtend = computed(() => {
  return {
    type: type.value
  }
})
const server = computed(() => {
  return {
    url: `/trip/${trip?.value?._id}/billing-and-costing/payment-note/list`,
    method: 'post'
  }
})
watch(
  () => type.value,
  () => refreshTables()
)

const refreshTables = (type?: string) => {
  if (dataTable.value) {
    dataTable.value.refresh()
  }
}

const openEdit = (data: any) => {
  emits('edit', data)
}

const handleView = (data: any) => {
  emits('view', data)
}

const freightekDialog = reactive({
  visible: false,
  loading: false,
  type: 'revenue',
  id: null
})
const freightekOptions = ref([
  { label: 'Revenue', value: 'revenue' },
  { label: 'Cost', value: 'cost' }
])
const openCreateInvoiceFreightek = (paymentNote: any) => {
  freightekDialog.id = paymentNote?._id
  freightekDialog.type = 'revenue'
  freightekDialog.visible = true
}

const createInvoiceFreightek = async () => {
  if (!freightekDialog.id) {
    return
  }
  freightekDialog.loading = true
  const api = {
    url: '/trip/tripId/billing-and-costing/invoice',
    method: 'post'
  }
  try {
    const data: any = await $api(api, {
      uriParams: { tripId: trip?.value?._id },
      body: {
        noteId: freightekDialog.id,
        type: freightekDialog.type
      }
    })

    const resData = data?.data?.value

    if (resData && resData.success) {
      freightekDialog.visible = false
      freightekDialog.id = null
      freightekDialog.type = 'revenue'
      if (resData.success) {
        process.client && $toast().success($t('labels.addSuccess'))
        refreshTables()
      }
    }
  } catch (e: any) {
    //
  }
  freightekDialog.loading = false
}

const checkPendingStatus = (debit: any) => {
  const status = debit?.status
  return !['active'].includes(status)
}

const openDelete = (debit: any) => {
  if (!debit) {
    return null
  }

  deletePaymentNote.value?.open(trip?.value?._id, debit._id)
}

const headers = ref([
  {
    field: 'code',
    headerName: $t('labels.code'),
    sortable: true,
    filter: 'agTextColumnFilter'
  },
  {
    field: 'status',
    headerName: $t('labels.status'),
    cellRenderer: InvoiceStatus,
    cellRendererParams: {
      keyStatus: 'status'
    },
    filter: 'TableFilterStatus',
    filterItems: [
      {
        name: $t('labels.all'),
        value: null
      },
      {
        name: 'Chờ xác nhận',
        value: 'pending',
        class: { '!bg-yellow-100': true, '!text-yellow-800': true }
      },
      {
        name: 'Xác nhận',
        value: 'actice',
        class: { '!bg-success-100': true, '!text-success-800': true } // Màu xanh nhạt (#81D4FA)
      }
    ],
    filterModelDefault: { type: 'equals', filterType: 'text' }
  },
  {
    field: 'customerName',
    headerName: 'Tên công ty',
    sortable: true,
    filter: 'agTextColumnFilter'
  },
  {
    field: 'selectInvoicingInformationName',
    headerName: $t('labels.invoicingInformation'),
    sortable: true,
    filter: 'agTextColumnFilter'
  },
  {
    field: 'invoicedDate',
    headerName: $t('labels.invoicedDate'),
    sortable: true,
    filter: 'agDateColumnFilter',
    valueGetter: (params: any) => {
      return $formatters.date(params.data.invoicedDate)
    }
  },
  {
    field: 'dueDate',
    headerName: $t('labels.dueDate'),
    sortable: true,
    filter: 'agDateColumnFilter',
    valueGetter: (params: any) => {
      return $formatters.date(params.data.dueDate)
    }
  },
  {
    field: 'totalAmount',
    headerName: 'Tổng tiền',
    sortable: true,
    filter: 'agNumberColumnFilter',
    valueGetter: (params: any) => {
      return $formatters.formatNumber(params.data.totalAmount)
    }
  },
  {
    field: 'totalAmount',
    headerName: 'Thuế',
    sortable: true,
    filter: 'agNumberColumnFilter',
    valueGetter: (params: any) => {
      return $formatters.formatNumber(params.data.totalTax)
    }
  },
  {
    field: 'totalAmount',
    headerName: 'Tổng tiền bao gồm thuế',
    sortable: true,
    filter: 'agNumberColumnFilter',
    valueGetter: (params: any) => {
      return $formatters.formatNumber(params.data.grandTotal)
    }
  },
  {
    field: 'currency',
    headerName: $t('labels.currency'),
    sortable: true,
    filter: 'agDateColumnFilter'
  },
  {
    field: 'paymentMethod',
    headerName: $t('labels.paymentMethod'),
    sortable: true,
    valueGetter: (params: any) => {
      const paymentMethod = params.data.paymentMethod
      if (paymentMethod === 'cash') {
        return 'Tiền mặt'
      }

      if (paymentMethod === 'bank') {
        return 'Chuyển khoản'
      }

      return ''
    }
  },
  {
    field: 'bankAccountInfo',
    headerName: $t('labels.bankAccount'),
    sortable: true,
    filter: 'agDateColumnFilter'
  },
  {
    field: 'createdAt',
    headerName: $t('labels.createdAt'),
    sortable: true,
    filter: 'agDateColumnFilter',
    valueGetter: (params: any) => {
      return $formatters.datetime(params.data.createdAt)
    }
  },
  {
    field: 'updatedAt',
    headerName: $t('labels.updatedAt'),
    sortable: true,
    filter: 'agDateColumnFilter',
    valueGetter: (params: any) => {
      return $formatters.datetime(params.data.updatedAt)
    }
  },
  {
    field: 'action',
    pinned: 'right',
    sortable: false,
    lockPosition: true,
    lockPinned: true,
    headerName: $t('labels.action'),
    cellStyle: { overflow: 'visible' },
    width: 300,
    maxWidth: 400,
    cellRenderer: ActionList,
    cellRendererParams: {
      storeKey: 'tripDataTable',
      storeName: 'Debit',
      items: [
        {
          icon: 'hi-eye-icon',
          label: $t('labels.view'),
          class: 'text-blue-600 hover:text-blue-500',
          permissions: checkPermission(layoutPermissions.trip_management),
          isAll: true,
          click: handleView
        },
        {
          icon: 'hi-pencil-square-icon',
          label: $t('labels.edit'),
          class: 'text-green-600 hover:text-green-500',
          permissions: checkPermission(layoutPermissions.trip_management),
          validate: checkPendingStatus,
          isAll: true,
          click: openEdit
        },
        {
          icon: 'hi-check-icon',
          label: 'Freightek',
          class: 'text-orange-600 hover:text-orange-500',
          permissions: checkPermission(layoutPermissions.trip_management),
          validate: checkPendingStatus,
          isAll: true,
          click: openCreateInvoiceFreightek
        },
        {
          icon: 'hi-trash-icon',
          label: $t('labels.delete'),
          class: 'text-error-500 hover:text-error-500',
          permissions: checkPermission(layoutPermissions.trip_management),
          click: openDelete,
          isArchive: true,
          validate: checkPendingStatus
        }
      ]
    }
  }
])

const usePaymentNoteArchive = {
  api: {
    url: `/trip/${trip?.value?._id}/billing-and-costing/payment-note/archive`,
    method: 'delete'
  },
  permissions: checkPermission(layoutPermissions.trip_management)
}

defineExpose({ refreshTables })
</script>
